// PARTS-SERVING-1 (2026-10-06): the four OPTIONAL per-hotspot fields on
// `GET /api/v1/diagram/{page}/hotspots` (bot service 5004) —
// `type_label`, `parts`, `details`, `links` (machineid-bot-integration
// API_CONTRACTS.md §Diagram Hotspots, PARTS-SERVING-1 bullet). Each is ABSENT
// when it has nothing to say; all text is already in the user's language.
//
// The bot checks every string before serving it (label <= 60 chars, value
// <= 200, no control / separator chars, none of < > `). We do not rely on
// that: the payload is raw wire data, so it is re-checked here and anything
// malformed is SKIPPED (never a crash, never a partial line):
//
//   line  {key, label, value}  — must be a plain object; `key`, `label` and
//         `value` must all be strings; label non-empty after trim.
//         key "not_drawn" -> the label alone (value ignored, contract: "").
//         any other key, KNOWN OR UNKNOWN -> `label: value`, value non-empty
//         after trim (an unknown key with a real label/value is shown rather
//         than dropped silently; an empty value has nothing to say -> skipped).
//   group  — must be an array of lines; a group with no valid line is dropped.
//   link  {page_id, label, primary} — page_id a positive safe integer (no
//         numeric strings), label a non-empty string; `primary` counts only
//         when it is exactly `true`. Duplicate page_ids: the first wins.
//         Primary = the first link flagged primary, else the first link.
//   Extra keys anywhere are ignored.
//
// Strings are trimmed and capped (code points, so a cap never splits a
// surrogate pair) at the contract limits. Rendering is plain text ONLY —
// React text nodes in DiagramInfoCardV2 / DiagramInfoCardExtrasV2, never
// innerHTML / dangerouslySetInnerHTML / markdown (SEC-REQ-5), so markup-like
// text is shown literally rather than filtered out.

/** Contract caps (API_CONTRACTS PARTS-SERVING-1). */
export const HOTSPOT_LINE_LABEL_MAX_CHARS = 60;
export const HOTSPOT_LINE_VALUE_MAX_CHARS = 200;

/** One `label: value` line. `value === ""` = show the label alone. */
export interface HotspotInfoLine {
  key: string;
  label: string;
  value: string;
}

/** A sheet to open in the overlay viewer (same document). */
export interface HotspotSheetLink {
  pageId: number;
  label: string;
}

export interface HotspotSheetLinks {
  primary: HotspotSheetLink;
  others: HotspotSheetLink[];
}

export interface HotspotExtras {
  typeLabel: string | null;
  parts: HotspotInfoLine[][];
  details: HotspotInfoLine[];
  links: HotspotSheetLinks | null;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Trim + cap a wire string; null when not a string or empty. */
export const capHotspotText = (raw: unknown, max: number): string | null => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const chars = Array.from(trimmed);
  if (chars.length <= max) return trimmed;
  return chars.slice(0, max).join("").trimEnd();
};

const parseLine = (raw: unknown): HotspotInfoLine | null => {
  if (!isPlainObject(raw)) return null;
  const { key, label, value } = raw;
  if (typeof key !== "string" || typeof value !== "string") return null;
  const cleanLabel = capHotspotText(label, HOTSPOT_LINE_LABEL_MAX_CHARS);
  if (!cleanLabel) return null;
  if (key === "not_drawn") return { key, label: cleanLabel, value: "" };
  const cleanValue = capHotspotText(value, HOTSPOT_LINE_VALUE_MAX_CHARS);
  if (!cleanValue) return null;
  return { key, label: cleanLabel, value: cleanValue };
};

const parseLines = (raw: unknown): HotspotInfoLine[] => {
  if (!Array.isArray(raw)) return [];
  const out: HotspotInfoLine[] = [];
  raw.forEach((entry) => {
    const line = parseLine(entry);
    if (line) out.push(line);
  });
  return out;
};

const parseGroups = (raw: unknown): HotspotInfoLine[][] => {
  if (!Array.isArray(raw)) return [];
  const out: HotspotInfoLine[][] = [];
  raw.forEach((group) => {
    if (!Array.isArray(group)) return;
    const lines = parseLines(group);
    if (lines.length > 0) out.push(lines);
  });
  return out;
};

const parseLinks = (raw: unknown): HotspotSheetLinks | null => {
  if (!Array.isArray(raw)) return null;
  const valid: Array<HotspotSheetLink & { flagged: boolean }> = [];
  raw.forEach((entry) => {
    if (!isPlainObject(entry)) return;
    const pageId = entry.page_id;
    if (
      typeof pageId !== "number" ||
      !Number.isSafeInteger(pageId) ||
      pageId <= 0
    ) {
      return;
    }
    const label = capHotspotText(entry.label, HOTSPOT_LINE_LABEL_MAX_CHARS);
    if (!label) return;
    if (valid.some((l) => l.pageId === pageId)) return;
    valid.push({ pageId, label, flagged: entry.primary === true });
  });
  if (valid.length === 0) return null;
  const primaryIdx = Math.max(0, valid.findIndex((l) => l.flagged));
  const strip = ({ pageId, label }: HotspotSheetLink) => ({ pageId, label });
  return {
    primary: strip(valid[primaryIdx]),
    others: valid.filter((_, i) => i !== primaryIdx).map(strip),
  };
};

/** Parse the four optional fields off a raw hotspot. Never throws. */
export const parseHotspotExtras = (hotspot: unknown): HotspotExtras => {
  const h = isPlainObject(hotspot) ? hotspot : {};
  return {
    typeLabel: capHotspotText(h.type_label, HOTSPOT_LINE_LABEL_MAX_CHARS),
    parts: parseGroups(h.parts),
    details: parseLines(h.details),
    links: parseLinks(h.links),
  };
};

/**
 * True when the hotspot carries anything the info card shows below the
 * description (parts / details / links). `type_label` alone does not count:
 * it only swaps the word on the existing type line.
 */
export const hasHotspotBodyExtras = (extras: HotspotExtras): boolean =>
  extras.parts.length > 0 || extras.details.length > 0 || extras.links !== null;
